/**
 *
 * Configuration for DataTable Action Buttons
 *
 * Most action buttons tend to be associated with row selections. Documentation
 * for the Select DataTables extension is here:
 * https://datatables.net/extensions/select/
 *
 */

import Radio from "backbone.radio";
import environment from "../../common/environment";
import confirmationAlert from "../../utils/alerts/confirmation";
import { numberFormatter } from "../../utils";

const channel = Radio.channel("report");

/* SHARED METHODS */

/**
 * Returns a boolean indicating whether the DataTable has data.
 * It is used to show/hide a DT button based on data availability.
 *
 * @param {DataTable} dt
 */
const available = (dt) => dt.data().any();

/**
 * Returns a DataTable's Ajax params without pagination properties.
 *
 * @param {DataTable} dt
 */
const getAjaxParams = (dt) => {
  const BLACKLIST = ["columns", "draw", "length", "order", "start"];

  // get ajax params
  const params = dt.table().ajax.params();

  // remove pagination params
  BLACKLIST.map((param) => {
    delete params[param];
  });

  return params;
};

export const genericButtons = {
  // minimum role requirement
  roles: ["ROLE_STANDARD"],
  // button configuration
  buttons: [
    {
      // button label
      text: '<i class="fa fa-fw fa-refresh"></i>',
      titleAttr: "Reload Data",
      // use generic availability check
      available,
      // click handler
      action(e, dt) {
        // reload the table without a callback while holding the current
        // page position.
        // https://datatables.net/reference/api/ajax.reload()
        dt.ajax.reload(null, false);
      },
    },
  ],
};

const routeButtons = {
  // our route
  "chart-reviews.projects": {
    // minimum role requirement
    roles: ["ROLE_ADMIN", "ROLE_CHART_REVIEW_ADMIN"],
    // button configuration
    buttons: [
      {
        eventName: "chart-reviews.project:create",
        text: "Create Project",
        action(e, dt, btn, config) {
          // announce event
          channel.trigger(config.eventName);
        },
      },
    ],
  },
  "chart-reviews.bulk-file-imports": {
    roles: ["ROLE_STANDARD"],
    buttons: [
      {
        eventName: "chart-reviews-bulk-import",
        text: "Import",
        action(e, dt, btn, config) {
          // announce event
          channel.trigger(config.eventName);
        },
      },
    ],
  },
  "chart-reviews.charts": {
    roles: ["ROLE_STANDARD"],
    buttons: [
      {
        extend: "collection",
        fade: 0,
        autoClose: true,
        text: 'Bulk Actions&nbsp;<span class="caret"></span>',
        available,
        buttons: [
          {
            eventName: "chart-reviews-bulk-edit",
            text: "Modify Results",
            available,
            action(e, dt, btn, config) {
              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload (special case for this bulk edit screen)
              const payload = {
                filters,
              };

              const total = dt.ajax.json().recordsTotal;

              const eventData = {
                payload,
                chartCount: total,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              channel.trigger(config.eventName, eventData, dt);
            },
          },
          {
            eventName: "chart-reviews-bulk-edit",
            extend: "selected",
            text: "Modify Selected",
            available,
            action(e, dt, btn, config) {
              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "id")) || [];

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload (special case for this bulk edit screen)
              const payload = {
                filters,
                ids: _.pluck(selectedIDs, "value"),
              };

              const total = selectedRows.length;

              const eventData = {
                payload,
                chartCount: total,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              channel.trigger(config.eventName, eventData, dt);
            },
          },
          {
            eventName: "chart-reviews-bulk-assign",
            text: "Reassign Results",
            available,
            action(e, dt, btn, config) {
              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload (special case for this bulk edit screen)
              const payload = {
                filters,
              };

              const total = dt.ajax.json().recordsTotal;

              const eventData = {
                payload,
                chartCount: total,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              channel.trigger(config.eventName, eventData, dt);
            },
          },
          {
            eventName: "chart-reviews-bulk-assign",
            extend: "selected",
            text: "Reassign Selected",
            available,
            action(e, dt, btn, config) {
              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "id")) || [];

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload (special case for this bulk edit screen)
              const payload = {
                filters,
                ids: _.pluck(selectedIDs, "value"),
              };

              const total = selectedRows.length;

              const eventData = {
                payload,
                chartCount: total,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              channel.trigger(config.eventName, eventData, dt);
            },
          },
        ],
      },
      {
        eventName: "chart-reviews.chart:create",
        text: "Create Chart",
        available() {
          const roles = ["ROLE_ADMIN", "ROLE_CHART_REVIEW_ADMIN"];

          return channel.request("user:has-any-role", roles);
        },
        action(e, dt, btn, config) {
          const eventData = {
            url: dt.ajax.url(),
          };

          // announce event
          channel.trigger(config.eventName, eventData);
        },
      },
    ],
  },
  "chart-reviews:members": {
    roles: ["ROLE_STANDARD"],
    buttons: [
      {
        eventName: "chart-reviews.member:selected",
        extend: "selectedSingle",
        text: "Use Selected Member",
        available,
        action(e, dt, btn, config) {
          // handles single row selection
          const data = dt.row({ selected: true }).data();

          // announce event
          channel.trigger(config.eventName, data);
        },
      },
    ],
  },
  "chart-reviews:providers": {
    roles: ["ROLE_STANDARD"],
    buttons: [
      {
        eventName: "chart-reviews.provider:selected",
        extend: "selectedSingle",
        text: "Use Selected Provider",
        available,
        action(e, dt, btn, config) {
          // handles single row selection
          const data = dt.row({ selected: true }).data();

          // announce event
          channel.trigger(config.eventName, data);
        },
      },
    ],
  },
  "chart-reviews:drg-edit:matchpoint-chooser": {
    roles: ["ROLE_CHART_REVIEW_ADMIN"],
    buttons: [
      {
        eventName: "chart-reviews:drg-edit:matchpoint-chosen",
        extend: "selectedSingle",
        text: "Use Selected Claim",
        available,
        action(e, dt, btn, config) {
          // handles single row selection
          const data = dt.row({ selected: true }).data();

          const eventData = {
            cmMatchSid: +data.cmMatchSid.value,
            matchPointClaimNbr: data.claimNbr.value,
            matchPointCommercialPlanId: data.claimCommercialPlanId.value,
            matchPointClaimMemberId: data.claimMemberId.value,
            matchPointClaimProviderId: data.claimProviderId.value,
            matchPointClaimFromDateOfService: data.claimFromDos.value,
            matchPointClaimThroughDateOfService: data.claimThruDos.value,
          };

          // announce event
          channel.trigger(config.eventName, eventData);
        },
      },
    ],
  },
  // Linked to from Deletes by Project -> # Pending Approval column
  "chart-reviews-diagnosis-review-groups-deletes-bulk-update": {
    roles: ["ROLE_ADMIN", "ROLE_CHART_REVIEW_ADMIN"],
    buttons: [
      {
        extend: "collection",
        fade: 0,
        autoClose: true,
        text: 'Bulk Actions&nbsp;<span class="caret"></span>',
        available,
        buttons: [
          {
            eventName: "chart-reviews:drg-deletes:bulk-update:filtered",
            returnRoute: "chart-reviews.deletes-by-project",
            text: "Approve Results",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "APPROVE" };

              // retrieve endpoint
              const endpoint = environment.getEndpoint(config.eventName);

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign({}, action, filters);

              const eventData = {
                endpoint,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = dt.ajax.json().recordsTotal;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to approve ${numberFormatter(
                total
              )} filtered result${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName:
              "chart-reviews:pending-deletes:bulk-update:invalidate-filtered",
            returnRoute: "chart-reviews.deletes-by-project",
            text: "Invalidate Results",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "INVALIDATE" };

              // retrieve endpoint
              const endpoint = environment.getEndpoint(config.eventName);

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign({}, action, filters);

              const eventData = {
                endpoint,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = dt.ajax.json().recordsTotal;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to invalidate ${numberFormatter(
                total
              )} filtered result${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName: "chart-reviews:drg-deletes:bulk-update:selected",
            returnRoute: "chart-reviews.deletes-by-project",
            extend: "selected",
            text: "Approve Selected",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "APPROVE" };

              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "id")) || [];

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // retrieve endpoint
              const endpoint = environment.getEndpoint(config.eventName);

              // compose payload
              const payload = Object.assign({}, filters, action, {
                ids: _.pluck(selectedIDs, "value"),
              });

              const eventData = {
                endpoint,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = selectedRows.length;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to approve ${numberFormatter(
                total
              )} selection${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName:
              "chart-reviews:pending-deletes:bulk-update:invalidate-selected",
            returnRoute: "chart-reviews.deletes-by-project",
            extend: "selected",
            text: "Invalidate Selected",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "INVALIDATE" };

              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "id")) || [];

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // retrieve endpoint
              const endpoint = environment.getEndpoint(config.eventName);

              // compose payload
              const payload = Object.assign({}, filters, action, {
                ids: _.pluck(selectedIDs, "value"),
              });

              const eventData = {
                endpoint,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = selectedRows.length;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to invalidate ${numberFormatter(
                total
              )} selection${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
        ],
      },
    ],
  },
  // Links to from Deletes by Project -> # Pending Chart Deletes column
  "chart-reviews-pending-chart-deletes-bulk-approval": {
    roles: ["ROLE_ADMIN", "ROLE_CHART_REVIEW_ADMIN"],
    buttons: [
      {
        extend: "collection",
        fade: 0,
        autoClose: true,
        text: 'Bulk Actions&nbsp;<span class="caret"></span>',
        available,
        buttons: [
          {
            eventName: "chart-reviews:pending-deletes:bulk-update:filtered",
            returnRoute: "chart-reviews.deletes-by-project",
            text: "Approve Results",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "APPROVE" };

              // retrieve endpoint
              const endpoint = environment.getEndpoint(config.eventName);

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign({}, action, filters);

              const eventData = {
                endpoint,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = dt.ajax.json().recordsTotal;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to approve ${numberFormatter(
                total
              )} filtered result${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName: "chart-reviews:pending-deletes:bulk-update:selected",
            returnRoute: "chart-reviews.deletes-by-project",
            extend: "selected",
            text: "Approve Selected",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "APPROVE" };

              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "cmDeletesSid")) || [];

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // retrieve endpoint
              const endpoint = environment.getEndpoint(config.eventName);

              // compose payload
              const payload = Object.assign({}, filters, action, {
                ids: _.pluck(selectedIDs, "value"),
              });

              const eventData = {
                endpoint,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = selectedRows.length;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to approve ${numberFormatter(
                total
              )} selection${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
        ],
      },
    ],
  },
  // Add Diags Bulk Update
  "chart-reviews-projects-*-diagnosis-review-groups-adds-bulk-update": {
    roles: ["ROLE_ADMIN", "ROLE_CHART_REVIEW_ADMIN"],
    buttons: [
      {
        extend: "collection",
        fade: 0,
        autoClose: true,
        text: 'Bulk Actions&nbsp;<span class="caret"></span>',
        available,
        buttons: [
          {
            eventName: "chart-reviews:drg-adds:bulk-update:filtered",
            text: "Approve Results",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "APPROVE" };

              // extract original route
              const returnRoute = dt.init().route;

              // grab first datatable row
              const firstRow = dt.row(0).data();
              // get project Id
              const projectId = (firstRow && firstRow.projectId.value) || null;
              // configure endpoint
              const endpoint = environment.getEndpoint(config.eventName, {
                projectId,
              });

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign({}, action, filters);

              const eventData = {
                returnRoute,
                endpoint,
                projectId,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = dt.ajax.json().recordsTotal;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to approve ${numberFormatter(
                total
              )} filtered result${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName: "chart-reviews:drg-adds:bulk-update:filtered",
            text: "Evaluate Results",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "EVALUATE" };

              // extract original route
              const returnRoute = dt.init().route;

              // grab first datatable row
              const firstRow = dt.row(0).data();
              // get project Id
              const projectId = (firstRow && firstRow.projectId.value) || null;
              // configure endpoint
              const endpoint = environment.getEndpoint(config.eventName, {
                projectId,
              });

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign({}, action, filters);

              const eventData = {
                returnRoute,
                endpoint,
                projectId,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = dt.ajax.json().recordsTotal;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to evaluate ${numberFormatter(
                total
              )} filtered result${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName: "chart-reviews:drg-adds:bulk-update:invalidate-filtered",
            text: "Invalidate Results",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "INVALIDATE" };

              // extract original route
              const returnRoute = dt.init().route;

              // grab first datatable row
              const firstRow = dt.row(0).data();
              // get project Id
              const projectId = (firstRow && firstRow.projectId.value) || null;
              // configure endpoint
              const endpoint = environment.getEndpoint(config.eventName, {
                projectId,
              });

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign({}, action, filters);

              const eventData = {
                returnRoute,
                endpoint,
                projectId,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = dt.ajax.json().recordsTotal;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to invalidate ${numberFormatter(
                total
              )} filtered result${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName: "chart-reviews:drg-adds:bulk-update:selected",
            extend: "selected",
            text: "Approve Selected",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "APPROVE" };

              // extract original route
              const returnRoute = dt.init().route;

              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // get project Id
              const projectId =
                (selectedRows &&
                  selectedRows[0] &&
                  selectedRows[0].projectId.value) ||
                null;
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "id")) || [];

              // configure endpoint with project id from first selected row
              const endpoint = environment.getEndpoint(config.eventName, {
                projectId,
              });

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign(
                {},
                { actionableType: filters["actionableType"] || null },
                action,
                { ids: _.pluck(selectedIDs, "value") }
              );

              const eventData = {
                returnRoute,
                endpoint,
                projectId,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = selectedRows.length;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to approve ${numberFormatter(
                total
              )} selection${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName: "chart-reviews:drg-adds:bulk-update:selected",
            extend: "selected",
            text: "Evaluate Selected",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "EVALUATE" };

              // extract original route
              const returnRoute = dt.init().route;

              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // get project Id
              const projectId =
                (selectedRows &&
                  selectedRows[0] &&
                  selectedRows[0].projectId.value) ||
                null;
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "id")) || [];

              // configure endpoint with project id from first selected row
              const endpoint = environment.getEndpoint(config.eventName, {
                projectId,
              });

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign(
                {},
                { actionableType: filters["actionableType"] || null },
                action,
                { ids: _.pluck(selectedIDs, "value") }
              );

              const eventData = {
                returnRoute,
                endpoint,
                projectId,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = selectedRows.length;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to evaluate ${numberFormatter(
                total
              )} selection${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName: "chart-reviews:drg-adds:bulk-update:invalidate-selected",
            extend: "selected",
            text: "Invalidate Selected",
            // titleAttr: "I am a tooltip",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "INVALIDATE" };

              // extract original route
              const returnRoute = dt.init().route;

              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // get project Id
              const projectId =
                (selectedRows &&
                  selectedRows[0] &&
                  selectedRows[0].projectId.value) ||
                null;
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "id")) || [];

              // configure endpoint with project id from first selected row
              const endpoint = environment.getEndpoint(config.eventName, {
                projectId,
              });

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign(
                {},
                { actionableType: filters["actionableType"] || null },
                action,
                { ids: _.pluck(selectedIDs, "value") }
              );

              const eventData = {
                returnRoute,
                endpoint,
                projectId,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = selectedRows.length;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to invalidate ${numberFormatter(
                total
              )} selection${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
        ],
      },
    ],
  },
  "admin.accounts": {
    roles: ["ROLE_ADMIN"],
    buttons: [
      {
        eventName: "admin.accounts:edit",
        extend: "selectedSingle",
        text: "View Account",
        available,
        action(e, dt, btn, config) {
          // handles single row selection
          const data = dt.row({ selected: true }).data();

          const eventData = {
            // selected row's ID
            id: +data.id.value,
          };

          // announce event
          channel.trigger(config.eventName, eventData);
        },
      },
      {
        eventName: "admin.accounts:create",
        text: "Create Account",
        action(e, dt, btn, config) {
          // announce event
          channel.trigger(config.eventName);
        },
      },
    ],
  },
  // Members modal -> Diag Delete Creation tab
  "members-*-claim-diags": {
    roles: ["ROLE_ADMIN", "ROLE_CHART_REVIEW_ADMIN"],
    buttons: [
      {
        extend: "collection",
        fade: 0,
        autoClose: true,
        text: 'Bulk Actions&nbsp;<span class="caret"></span>',
        available,
        buttons: [
          {
            eventName: "members:claim-diag-deletes:bulk-update:filtered",
            text: "Delete Results",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "APPROVE" };

              // grab first datatable row
              const firstRow = dt.row(0).data();
              // get member Id
              const memberId = (firstRow && firstRow.memberId.value) || null;
              // configure endpoint
              const endpoint = environment.getEndpoint(config.eventName, {
                id: memberId,
              });

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign({}, action, filters);

              // extract original route and pass it as the return route
              const returnRoute = dt.init().route;

              const eventData = {
                returnRoute,
                endpoint,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = dt.ajax.json().recordsTotal;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to delete ${numberFormatter(
                total
              )} filtered result${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
          {
            eventName: "members:claim-diag-deletes:bulk-update:selected",
            extend: "selected",
            text: "Delete Selected",
            available,
            action(e, dt, btn, config) {
              // action
              const action = { actionToTake: "APPROVE" };

              // handles single or multiple row selection
              const selectedRows = dt.rows({ selected: true }).data();
              // plucks IDs
              const selectedIDs =
                (selectedRows && _.pluck(selectedRows, "id")) || [];
              // get member Id
              const memberId =
                (selectedRows && _.first(_.pluck(selectedRows, "memberId"))) ||
                null;
              // configure endpoint
              const endpoint = environment.getEndpoint(config.eventName, {
                id: (memberId && memberId.value) || null,
              });

              // create filters from ajax params
              const filters = getAjaxParams(dt);

              // compose payload
              const payload = Object.assign({}, filters, action, {
                ids: _.pluck(selectedIDs, "value"),
              });

              // extract original route and pass it as the return route
              const returnRoute = dt.init().route;

              const eventData = {
                returnRoute,
                endpoint,
                payload,
              };

              // enable processing mode on this button
              this.processing(true);

              // announce event
              const total = selectedRows.length;
              const plural = total > 1 ? "s" : "";
              const title = `You have chosen to delete ${numberFormatter(
                total
              )} selection${plural}.`;
              confirmationAlert({ title }).then((result) => {
                result.value &&
                  channel.trigger(config.eventName, eventData, dt);
              });
            },
          },
        ],
      },
    ],
  },
};

export { routeButtons as default };
